import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Paper, Chip, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, IconButton,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Cell,
} from 'recharts';
import {
  School as SchoolIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { learningAnalyticsAPI, SchoolPerformanceData, SchoolPerformanceSchool } from '../../services/learningAnalyticsApi';

const CATEGORY_COLORS: Record<string, string> = {
  'Excellent': '#388e3c',
  'Good': '#1976d2',
  'Average': '#fbc02d',
  'Needs Improvement': '#d32f2f',
};

const CATEGORY_CHIP_COLORS: Record<string, 'success' | 'primary' | 'warning' | 'error'> = {
  'Excellent': 'success',
  'Good': 'primary',
  'Average': 'warning',
  'Needs Improvement': 'error',
};

type Order = 'asc' | 'desc';

const SchoolPerformanceTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SchoolPerformanceData | null>(null);
  const [year, setYear] = useState<string>('');
  const [district, setDistrict] = useState<string>('');
  const [level, setLevel] = useState<string>('');
  const [orderBy, setOrderBy] = useState<string>('composite_score');
  const [order, setOrder] = useState<Order>('desc');
  const [radarSchool, setRadarSchool] = useState<SchoolPerformanceSchool | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (year) params.year = parseInt(year);
      if (district) params.district = district;
      if (level) params.level = parseInt(level);

      const response = await learningAnalyticsAPI.getSchoolPerformance(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load school performance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, district, level]);

  const handleSort = (field: string) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const sortedSchools = data?.schools
    ? [...data.schools].sort((a: any, b: any) => {
        const aVal = a[orderBy] ?? 0;
        const bVal = b[orderBy] ?? 0;
        return order === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
      })
    : [];

  const districts = data?.schools
    ? Array.from(new Set(data.schools.map(s => s.district_name))).sort()
    : [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const top20 = sortedSchools.slice(0, 20);
  const categoryData = [
    { name: 'Excellent', value: data.summary.excellent_count, color: CATEGORY_COLORS['Excellent'] },
    { name: 'Good', value: data.summary.good_count, color: CATEGORY_COLORS['Good'] },
    { name: 'Average', value: data.summary.average_count, color: CATEGORY_COLORS['Average'] },
    { name: 'Needs Improvement', value: data.summary.needs_improvement_count, color: CATEGORY_COLORS['Needs Improvement'] },
  ];

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Year" value={year}
              onChange={(e) => setYear(e.target.value)}>
              <MenuItem value="">Current</MenuItem>
              {[2026, 2025, 2024, 2023, 2022].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)}>
              <MenuItem value="">All Districts</MenuItem>
              {districts.map(d => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="School Level" value={level}
              onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="3">Pre-Primary</MenuItem>
              <MenuItem value="1">Primary</MenuItem>
              <MenuItem value="2">Secondary</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <SchoolIcon color="primary" />
                <Typography variant="body2" color="textSecondary">Total Schools</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{data.summary.total_schools}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingUpIcon color="info" />
                <Typography variant="body2" color="textSecondary">Avg Score</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{data.summary.avg_composite_score}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <StarIcon sx={{ color: '#388e3c' }} />
                <Typography variant="body2" color="textSecondary">Excellent</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="success.main">{data.summary.excellent_count}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WarningIcon color="error" />
                <Typography variant="body2" color="textSecondary">Needs Improvement</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="error.main">{data.summary.needs_improvement_count}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Category Distribution Chart */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Performance Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={130} />
                  <Tooltip />
                  <Bar dataKey="value" name="Schools">
                    {categoryData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Schools Bar Chart */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top 20 Schools by Composite Score</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={top20} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="school_name" type="category" width={160} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => `${v}`} />
                  <Bar dataKey="composite_score" name="Score">
                    {top20.map((entry, idx) => (
                      <Cell key={idx} fill={CATEGORY_COLORS[entry.category] || '#1976d2'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* School Rankings Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>School Performance Rankings</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Click any row to see the radar chart breakdown
          </Typography>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { id: 'rank', label: '#' },
                    { id: 'school_name', label: 'School' },
                    { id: 'district_name', label: 'District' },
                    { id: 'composite_score', label: 'Score' },
                    { id: 'category', label: 'Category' },
                    { id: 'student_attendance_rate', label: 'Student Att%' },
                    { id: 'teacher_attendance_rate', label: 'Teacher Att%' },
                    { id: 'student_teacher_ratio', label: 'STR' },
                    { id: 'infrastructure_score', label: 'Infra' },
                  ].map(col => (
                    <TableCell key={col.id}>
                      <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'}
                        onClick={() => handleSort(col.id)}>
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSchools.map((school) => (
                  <TableRow key={school.school_reg_number} hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setRadarSchool(school)}>
                    <TableCell>{school.rank}</TableCell>
                    <TableCell>{school.school_name}</TableCell>
                    <TableCell>{school.district_name}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">{school.composite_score}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={school.category} size="small"
                        color={CATEGORY_CHIP_COLORS[school.category] || 'default'} />
                    </TableCell>
                    <TableCell>{school.student_attendance_rate ?? '-'}</TableCell>
                    <TableCell>{school.teacher_attendance_rate ?? '-'}</TableCell>
                    <TableCell>{school.student_teacher_ratio ?? '-'}</TableCell>
                    <TableCell>{school.infrastructure_score}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Radar Chart Dialog */}
      <Dialog open={!!radarSchool} onClose={() => setRadarSchool(null)} maxWidth="sm" fullWidth>
        {radarSchool && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6">{radarSchool.school_name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {radarSchool.district_name} | Score: {radarSchool.composite_score} |
                    <Chip label={radarSchool.category} size="small" sx={{ ml: 1 }}
                      color={CATEGORY_CHIP_COLORS[radarSchool.category] || 'default'} />
                  </Typography>
                </Box>
                <IconButton onClick={() => setRadarSchool(null)}><CloseIcon /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart data={[
                  { metric: 'Student Attendance', value: radarSchool.normalized.student_attendance },
                  { metric: 'Teacher Attendance', value: radarSchool.normalized.teacher_attendance },
                  { metric: 'Enrollment Stability', value: radarSchool.normalized.enrollment_stability },
                  { metric: 'Staff-Student Ratio', value: radarSchool.normalized.student_teacher_ratio },
                  { metric: 'Infrastructure', value: radarSchool.normalized.infrastructure },
                ]}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  <Radar dataKey="value" stroke="#1976d2" fill="#1976d2" fillOpacity={0.3} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SchoolPerformanceTab;
